import { useState } from 'react'
import type { FormEvent } from 'react'
import { api, ApiError } from '@/lib/api'
import type { EventInfo, Me, ValidationDetails, VerificationInfo } from '@/lib/api'
import { VerificationForm } from '@/pages/play/VerificationForm'

type Props = { event: EventInfo; onRegistered: (me: Me) => void }

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

type FieldErrors = { email?: string; location?: string }

export function RegistrationForm({ event, onRegistered }: Props) {
  const [email, setEmail] = useState('')
  const [locationCode, setLocationCode] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [verification, setVerification] = useState<VerificationInfo | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const requiresLocation = event.registration.requiresLocation

  function validate(): FieldErrors {
    const errors: FieldErrors = {}
    if (!EMAIL_PATTERN.test(email.trim())) errors.email = 'Enter a valid email address'
    if (requiresLocation && !locationCode) errors.location = `Choose your ${event.locationLabel.toLowerCase()}`
    return errors
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setFormError(null)
    const errors = validate()
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    try {
      const me = await api.register(event.slug, {
        email: email.trim(),
        ...(requiresLocation ? { locationCode } : {}),
      })
      onRegistered(me)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'REVERIFICATION_REQUIRED') setVerification(verificationFromDetails(err.details))
        else if (err.code === 'VALIDATION_FAILED' && err.details) setFieldErrors(fromDetails(err.details as ValidationDetails))
        else setFormError(err.message)
      } else {
        setFormError('Network error. Check your connection and try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (verification) {
    return (
      <VerificationForm
        event={event}
        email={email.trim()}
        initial={verification}
        onVerified={onRegistered}
        onBack={() => setVerification(null)}
      />
    )
  }

  return (
    <main>
      <h1>{event.name}</h1>
      {event.description && <p className="muted">{event.description}</p>}

      {!event.available && (
        <p className="card status-err" role="alert">
          This event is not open right now.
        </p>
      )}

      <form className="card" onSubmit={(e) => void handleSubmit(e)} noValidate>
        {requiresLocation && (
          <div className="field">
            <label htmlFor="location">{event.locationLabel}</label>
            <select
              id="location"
              value={locationCode}
              onChange={(e) => setLocationCode(e.target.value)}
              aria-invalid={Boolean(fieldErrors.location)}
              aria-describedby={fieldErrors.location ? 'location-error' : undefined}
              disabled={submitting}
            >
              <option value="">Select…</option>
              {event.locations.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
            {fieldErrors.location && (
              <p id="location-error" className="field-error">
                {fieldErrors.location}
              </p>
            )}
          </div>
        )}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
            disabled={submitting}
          />
          {fieldErrors.email && (
            <p id="email-error" className="field-error">
              {fieldErrors.email}
            </p>
          )}
        </div>

        {formError && (
          <p className="field-error" role="alert">
            {formError}
          </p>
        )}

        <button type="submit" className="primary" disabled={submitting || !event.available}>
          {submitting ? 'Starting…' : 'Start'}
        </button>
      </form>
    </main>
  )
}

function verificationFromDetails(details: unknown): VerificationInfo {
  const v = (details as { verification?: Partial<VerificationInfo> } | undefined)?.verification
  return {
    codeSent: v?.codeSent ?? true,
    expiresInSeconds: v?.expiresInSeconds ?? 600,
    resendAvailableInSeconds: v?.resendAvailableInSeconds ?? 60,
  }
}

function fromDetails(details: ValidationDetails): FieldErrors {
  const errors: FieldErrors = {}
  if (details.email?.length) errors.email = `Email ${details.email[0]}`
  if (details.location?.length) errors.location = `Location ${details.location[0]}`
  return errors
}
